import moment from "moment";
import {ProductSalesPeriodMode} from "../entities/Order/ProductSales";

export type ProductSalesPeriodRange = {
    from: string;
    to: string;
};

export function defaultProductSalesPeriodAnchor(mode: ProductSalesPeriodMode): string {
    const now = moment();
    switch (mode) {
        case "week":
            return now.format("YYYY-[W]WW");
        case "month":
            return now.format("YYYY-MM");
        case "year":
            return now.format("YYYY");
        default:
            return now.format("YYYY-MM-DD");
    }
}

export function resolveProductSalesPeriod(
    mode: ProductSalesPeriodMode,
    anchor: string,
    customFrom?: string,
    customTo?: string,
): ProductSalesPeriodRange {
    switch (mode) {
        case "week": {
            const start = moment(anchor, "YYYY-[W]WW", true).startOf("week");
            const end = start.clone().endOf("week");
            return {
                from: start.format("YYYY-MM-DD"),
                to: end.format("YYYY-MM-DD"),
            };
        }
        case "month": {
            const start = moment(anchor, "YYYY-MM", true).startOf("month");
            const end = start.clone().endOf("month");
            return {
                from: start.format("YYYY-MM-DD"),
                to: end.format("YYYY-MM-DD"),
            };
        }
        case "year": {
            const start = moment(anchor, "YYYY", true).startOf("year");
            const end = start.clone().endOf("year");
            return {
                from: start.format("YYYY-MM-DD"),
                to: end.format("YYYY-MM-DD"),
            };
        }
        case "custom":
        default: {
            const from = customFrom || moment().startOf("month").format("YYYY-MM-DD");
            const to = customTo || moment().format("YYYY-MM-DD");
            return {from, to};
        }
    }
}

export function formatProductSalesPeriodLabel(
    mode: ProductSalesPeriodMode,
    anchor: string,
    customFrom?: string,
    customTo?: string,
): string {
    const range = resolveProductSalesPeriod(mode, anchor, customFrom, customTo);
    const fromLabel = moment(range.from, "YYYY-MM-DD", true).format("MM/DD/YYYY");
    const toLabel = moment(range.to, "YYYY-MM-DD", true).format("MM/DD/YYYY");
    return `${fromLabel} - ${toLabel}`;
}

export const ENGLISH_MONTHS: {value: string; label: string}[] = [
    {value: "01", label: "January"},
    {value: "02", label: "February"},
    {value: "03", label: "March"},
    {value: "04", label: "April"},
    {value: "05", label: "May"},
    {value: "06", label: "June"},
    {value: "07", label: "July"},
    {value: "08", label: "August"},
    {value: "09", label: "September"},
    {value: "10", label: "October"},
    {value: "11", label: "November"},
    {value: "12", label: "December"},
];

export function parseMonthAnchor(anchor: string): {year: string; month: string} {
    const parsed = moment(anchor, "YYYY-MM", true);
    if (!parsed.isValid()) {
        const now = moment();
        return {year: now.format("YYYY"), month: now.format("MM")};
    }
    return {year: parsed.format("YYYY"), month: parsed.format("MM")};
}

export function buildMonthAnchor(year: string, month: string): string {
    return `${year}-${month}`;
}

export function weekAnchorToDate(anchor: string): Date | null {
    const parsed = moment(anchor, "YYYY-[W]WW", true);
    return parsed.isValid() ? parsed.startOf("week").toDate() : null;
}

export function dateToWeekAnchor(date: Date | null): string {
    return date ? moment(date).format("YYYY-[W]WW") : defaultProductSalesPeriodAnchor("week");
}

export function dateStringToDate(value: string): Date | null {
    const parsed = moment(value, "YYYY-MM-DD", true);
    return parsed.isValid() ? parsed.toDate() : null;
}

export function dateToDateString(date: Date | null): string {
    return date ? moment(date).format("YYYY-MM-DD") : "";
}
